/**
 * Worst-of computation for the MDF "Claims" detail-page tab icon.
 *
 * The Claims tab renders EVERY gating step it displays — Step 2 (PO/4J
 * approval), Step 3 (invoice present), Step 4 (entity linkage), Step 5
 * (cost-share), Step 6 (line-item eligibility), Step 7 (duplicate invoice) and
 * Step 8 (sponsorship eligibility). Its label icon must reflect the worst
 * verdict across ALL of them: a reject or escalate in ANY step must not leave
 * the tab green just because the others passed (batpur R4).
 *
 * Steps 7 and 8 were previously omitted, so a Step-7 MDFDuplicate reject (or a
 * Step-8 sponsorship reject) left the Claims tab green while the Approval
 * Summary and the final decision showed reject — a contradiction reviewers hit
 * on a real multi-claim FR whose only failure was the intra-FR duplicate check.
 *
 * MDF steps emit pass | reject | escalate. Precedence for the tab icon:
 *   reject  (a hard failure)         wins over
 *   escalate (needs human review)    wins over
 *   pass.
 */

// The steps the Claims tab surfaces, in display order. The first present
// status is the fallback when nothing rejects/escalates (so a tab whose checks
// are all pending/absent still renders a sensible icon).
export const MDF_CLAIMS_TAB_STEPS = [
  'step_2_po_4j_approval',
  'step_3_invoice_present',
  'step_4_entity_linkage',
  'step_5_cost_share',
  'step_6_line_item_eligibility',
  'step_7_duplicate_invoice',
  'step_8_sponsorship_eligibility',
];

/**
 * Reduce the per-step check map to the single worst status for the tab icon.
 * @param {Object} checks - map of step key -> { status: 'pass'|'reject'|'escalate', ... }
 * @param {string[]} [steps] - step keys to consider (defaults to the Claims tab steps)
 * @returns {string} 'reject' | 'escalate' | 'pass' | '' (empty when no status present)
 */
export function mdfClaimsTabStatus(checks, steps = MDF_CLAIMS_TAB_STEPS) {
  const byStep = checks || {};
  const statuses = steps
    .map((k) => ((byStep[k] && byStep[k].status) || '').toLowerCase())
    .filter(Boolean);
  if (statuses.includes('reject')) return 'reject';
  if (statuses.includes('escalate')) return 'escalate';
  return statuses[0] || '';
}
